/**
 * Contacts surface (live) - client-side .ics generation for a contact's stored
 * follow-up reminder. Reminders are delivered via the user's calendar (no
 * system-delivered notification is implied); this builds an all-day VEVENT for
 * the reminder date and triggers a download. Pure client-side, no writes.
 */

import type { Contact } from "./types";

/** YYYYMMDD from an ISO date (UTC), for an all-day DATE value. */
function icsDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

/** Next day (all-day DTEND is exclusive). */
function icsDatePlusOne(iso: string): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + 1);
  return icsDate(d.toISOString());
}

function icsStamp(now: Date): string {
  return now.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function escapeText(value: string): string {
  return value.replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
}

export function buildIcs(contact: Contact, now: Date = new Date()): string {
  const date = contact.followUpDueAt;
  if (!date) return "";
  const summary = escapeText(`Follow up with ${contact.name || "contact"}`);
  const description = escapeText(
    [contact.company, contact.email].filter(Boolean).join(" · "),
  );
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//BDSE//Contacts//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:contact-${contact.id}-followup@bdsignalengine`,
    `DTSTAMP:${icsStamp(now)}`,
    `DTSTART;VALUE=DATE:${icsDate(date)}`,
    `DTEND;VALUE=DATE:${icsDatePlusOne(date)}`,
    `SUMMARY:${summary}`,
    description ? `DESCRIPTION:${description}` : "DESCRIPTION:",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadIcs(contact: Contact): void {
  const text = buildIcs(contact);
  if (!text || typeof document === "undefined") return;
  const blob = new Blob([text], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = (contact.name || "contact").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  a.download = `follow-up-${slug}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
